import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollRestorer from "@/components/ScrollRestorer";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Clubs from "@/pages/Clubs";
import Tournaments from "@/pages/Tournaments";
import Retas from "@/pages/Retas";
import PadelCenter from "@/pages/PadelCenter";
import { PadelCenterProvider } from "@/context/PadelCenterContext";
import Clinics from "@/pages/Clinics";
import AtomEcosystem from "@/pages/AtomEcosystem";
import AdminDashboardTheme from "@/pages/AdminDashboardTheme";
import VestuarioVirtual from "@/pages/VestuarioVirtual";
import ClubARView from "@/pages/ClubARView";
import ClubsARPicker from "@/pages/ClubsARPicker";
import CourtRentals from "@/pages/CourtRentals";
import Statistics from "@/pages/Statistics";
import Streaming from "@/pages/Streaming";
import PlayerProfile from "@/pages/PlayerProfile";
import PaymentHistory from "@/pages/PaymentHistory";
import Invitations from "@/pages/Invitations";
import PaymentSettings from "@/pages/PaymentSettings";
import UserManagement from "@/pages/UserManagement";
import PlayersDirectory from "@/pages/PlayersDirectory";
import ProfileEdit from "@/pages/ProfileEdit";
import ClubDetail from "@/pages/ClubDetail";
import Chat from "@/pages/Chat";
import MenuLayoutAdmin from "@/pages/MenuLayoutAdmin";
import ClubDashboard from "@/pages/ClubDashboard";
import Shop from "@/pages/Shop";
import RevenueDashboard from "@/pages/RevenueDashboard";
import AdminCleanup from "@/pages/AdminCleanup";
import WorldHeatmap from "@/pages/WorldHeatmap";
import Agenda from "@/pages/Agenda";
import MyWalletCard from "@/pages/MyWalletCard";
import DydAccess from "@/pages/DydAccess";
import Trivia from "@/pages/Trivia";
import AtariTennis from "@/pages/AtariTennis";
import DydCoach from "@/pages/DydCoach";
import DydCoin from "@/pages/DydCoin";
import PlatformControl from "@/pages/PlatformControl";
import DevDashboard from "@/pages/DevDashboard";
import { useAuth as _useAuthForDevRedirect } from "@/context/AuthContext";
import { Navigate as _NavigateForDevRedirect } from "react-router-dom";

function DashboardOrDev() {
  const { user } = _useAuthForDevRedirect();
  if (user?.role === "desarrollador") {
    const granted = user?.dyd_access?.granted_modules || [];
    if (!granted.includes("inicio")) return <_NavigateForDevRedirect to="/dashboard/dev" replace />;
  }
  return <Dashboard />;
}
import PricingPlans from "@/pages/PricingPlans";
import Wearables from "@/pages/Wearables";
import Leagues from "@/pages/Leagues";
import { MatchesList, MatchDetail } from "@/pages/Matches";
import PublicPlayer from "@/pages/PublicPlayer";
import PublicClub from "@/pages/PublicClub";
import HeadToHead from "@/pages/HeadToHead";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PadelGame from "@/pages/PadelGame";
import AdminFeatureFlags from "@/pages/AdminFeatureFlags";
import AdminGearCatalog from "@/pages/AdminGearCatalog";
import WalletScan from "@/pages/WalletScan";
import Balonpie from "@/pages/Balonpie";
import AdminVisibility from "@/pages/AdminVisibility";
import AdminAccessControl from "@/pages/AdminAccessControl";
import Community from "@/pages/Community";
import Comics from "@/pages/Comics";
import PlatformUsage from "@/pages/PlatformUsage";
import RaceDetail, { RacesList, RaceAdminDashboard } from "@/pages/Races";
import TournamentGroups from "@/pages/TournamentGroups";
import LeagueGroups from "@/pages/LeagueGroups";
import AdminPublicidad from "@/pages/AdminPublicidad";
import DydRun from "@/pages/DydRun";
import Reportes from "@/pages/Reportes";
import Recuperacion from "@/pages/Recuperacion";
import ManualPayments from "@/pages/ManualPayments";
import PartnerMatch from "@/pages/PartnerMatch";
import CommunityLive from "@/pages/CommunityLive";
import InstallAppBanner from "@/components/InstallAppBanner";
import { useLang } from "@/context/LanguageContext";

/**
 * Bridge: when the authenticated user has a stored `language` preference, sync
 * it into the LanguageContext on every auth change. The user's explicit toggle
 * is honoured first via `userPicked`.
 */
function LanguageSync() {
  const { user } = useAuth();
  const { setLang, userPicked, lang } = useLang();
  React.useEffect(() => {
    if (!user || userPicked) return;
    const target = user.language;
    if ((target === "es" || target === "en") && target !== lang) {
      setLang(target);
    }
  }, [user, userPicked, setLang, lang]);
  return null;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0A0A0A]">
        <div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <LanguageProvider>
    <ThemeProvider>
    <AuthProvider>
    <CurrencyProvider>
    <PadelCenterProvider>
      <BrowserRouter>
        <ScrollRestorer />
        <LanguageSync />
        <Routes>
          <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
          <Route path="/p/:userId" element={<PublicPlayer />} />
          <Route path="/c/:clubId" element={<PublicClub />} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index element={<DashboardOrDev />} />
            <Route path="clubes" element={<Clubs />} />
            <Route path="clubes/:clubId" element={<ClubDetail />} />
            <Route path="chat" element={<Chat />} />
            <Route path="menu-layout" element={<MenuLayoutAdmin />} />
            <Route path="clubes/:clubId/panel" element={<ClubDashboard />} />
            <Route path="tienda" element={<Shop />} />
            <Route path="torneos" element={<Tournaments />} />
            <Route path="retas" element={<Retas />} />
            <Route path="padel-center" element={<PadelCenter />} />
            <Route path="clinicas" element={<Clinics />} />
            <Route path="atomo" element={<AtomEcosystem />} />
            <Route path="admin/dashboard-theme" element={<AdminDashboardTheme />} />
            <Route path="vestuario-virtual" element={<VestuarioVirtual />} />
            <Route path="vestuario-virtual/:clubId" element={<VestuarioVirtual />} />
            <Route path="clubes/:clubId/ar" element={<ClubARView />} />
            <Route path="clubes-ar" element={<ClubsARPicker />} />
            <Route path="canchas" element={<CourtRentals />} />
            <Route path="estadisticas" element={<Statistics />} />
            <Route path="estadisticas/h2h" element={<HeadToHead />} />
            <Route path="streaming" element={<Streaming />} />
            <Route path="jugador/:userId" element={<PlayerProfile />} />
            <Route path="pagos" element={<PaymentHistory />} />
            <Route path="invitaciones" element={<Invitations />} />
            <Route path="config-pagos" element={<PaymentSettings />} />
            <Route path="usuarios" element={<UserManagement />} />
            <Route path="ingresos" element={<RevenueDashboard />} />
            <Route path="limpiar-historial" element={<AdminCleanup />} />
            <Route path="mapa-jugadores" element={<WorldHeatmap />} />
            <Route path="agenda" element={<Agenda />} />
            <Route path="mi-tarjeta" element={<MyWalletCard />} />
            <Route path="dyd-access" element={<DydAccess />} />
            <Route path="trivia" element={<Trivia />} />
            <Route path="atari" element={<AtariTennis />} />
            <Route path="dyd-coach" element={<DydCoach />} />
            <Route path="dyd-coin" element={<DydCoin />} />
            <Route path="plataforma" element={<PlatformControl />} />
            <Route path="dev" element={<DevDashboard />} />
            <Route path="planes" element={<PricingPlans />} />
            <Route path="wearables" element={<Wearables />} />
            <Route path="ligas" element={<Leagues />} />
            <Route path="jugadores" element={<PlayersDirectory />} />
            <Route path="marcador" element={<MatchesList />} />
            <Route path="marcador/:id" element={<MatchDetail />} />
            <Route path="padel-game" element={<PadelGame />} />
            <Route path="admin-flags" element={<AdminFeatureFlags />} />
            <Route path="admin-gear" element={<AdminGearCatalog />} />
            <Route path="wallet-scan" element={<WalletScan />} />
            <Route path="balonpie" element={<Balonpie />} />
            <Route path="admin-visibility" element={<AdminVisibility />} />
            <Route path="admin-access" element={<AdminAccessControl />} />
            <Route path="comunidad" element={<Community />} />
            <Route path="comic" element={<Comics />} />
            <Route path="uso-plataforma" element={<PlatformUsage />} />
            <Route path="dyd-run/carreras" element={<RacesList />} />
            <Route path="dyd-run/carreras/:raceId" element={<RaceDetail />} />
            <Route path="dyd-run/carreras/:raceId/admin" element={<RaceAdminDashboard />} />
            <Route path="dyd-run/carreras/:raceId/exito" element={<RaceDetail />} />
            <Route path="torneos/:id/grupos" element={<TournamentGroups />} />
            <Route path="ligas/:id/grupos" element={<LeagueGroups />} />
            <Route path="publicidad" element={<AdminPublicidad />} />
            <Route path="dyd-run" element={<DydRun />} />
            <Route path="reportes" element={<Reportes />} />
            <Route path="recuperacion" element={<Recuperacion />} />
            <Route path="pagos-manuales" element={<ManualPayments />} />
            <Route path="partner-match" element={<PartnerMatch />} />
            <Route path="comunidad-live" element={<CommunityLive />} />
            <Route path="mi-perfil" element={<ProfileEdit />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster
          position="top-right"
          toastOptions={{
            style: { background: "#1A1A1A", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" },
          }}
        />
        <InstallAppBanner />
      </BrowserRouter>
    </PadelCenterProvider>
    </CurrencyProvider>
    </AuthProvider>
    </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
